
export const PROVIDER_OPTIONS = [
  {
    value: "KEYCLOAK",
    label: "Keycloak",
    ready: true,
  },
  {
    value: "ENTRA_ID",
    label: "Entra ID",
    ready: true,
  },
  {
    value: "COGNITO",
    label: "AWS Cognito",
    ready: true,
  },
  {
    value: "AUTH0",
    label: "Auth0",
    ready: false,
  },
  {
    value: "GENERIC_OIDC",
    label: "Generic OIDC",
    ready: false,
  },
];

export function presetFor(provider) {
  const shared = {
    enabled: true,
    scopes: "openid, profile, email",

    redirectUri:
      "http://localhost:8080/login/oauth2/code/acme-kc",

    postLoginRedirectUri:
      "http://localhost:3000/auth/callback",

    clientId: "cestaconnect-sso",
    clientSecret: "",

    realm: "",
    rolesClaim: "",

    tenantId: "",
    region: "",
    userPoolId: "",
    domain: "",

    endSessionEndpoint: "",
  };

  switch (provider) {
    case "KEYCLOAK":
      return {
        ...shared,

        name: "Acme Keycloak",
        tenantKey: "acme-kc",
        providerType: "KEYCLOAK",
        authMode: "LOCAL_JWT",

        issuerUri:
          "http://host.docker.internal:8180/realms/acme",

        redirectUri:
          "http://localhost:8080/login/oauth2/code/acme-kc",

        endSessionEndpoint:
          "http://localhost:8180/realms/acme/protocol/openid-connect/logout",

        realm: "acme",
        rolesClaim: "realm_access.roles",
      };

    case "ENTRA_ID":
      return {
        ...shared,

        name: "Acme Entra ID",
        tenantKey: "acme-entra",
        providerType: "ENTRA_ID",
        authMode: "LOCAL_JWT",

        issuerUri:
          "https://login.microsoftonline.com/{tenantId}/v2.0",

        redirectUri:
          "http://localhost:8080/login/oauth2/code/acme-entra",

        endSessionEndpoint:
          "https://login.microsoftonline.com/{tenantId}/oauth2/v2.0/logout",

        clientId: "",
        clientSecret: "",

        tenantId: "",
        rolesClaim: "roles",
      };

    case "COGNITO":
      return {
        ...shared,

        name: "Acme Cognito",
        tenantKey: "acme-cognito",
        providerType: "COGNITO",
        authMode: "LOCAL_JWT",

        issuerUri:
          "https://cognito-idp.{region}.amazonaws.com/{userPoolId}",

        redirectUri:
          "http://localhost:8080/login/oauth2/code/acme-cognito",

        clientId: "",
        clientSecret: "",

        region: "",
        userPoolId: "",
        domain: "",

        rolesClaim: "cognito:groups",
      };

    default:
      return {
        ...shared,

        name: "",
        tenantKey: "",
        providerType: provider,
        authMode: "HANDOVER",

        issuerUri: "",

        redirectUri:
          "http://localhost:8080/login/oauth2/code/",
      };
  }
}

export function toIdpPayload(form) {
  const scopes = form.scopes
    .split(",")
    .map((scope) => scope.trim())
    .filter(Boolean);

  const claimMappings = {
    sub: "sub",
    email: "email",
    name: "name",
  };

  if (form.rolesClaim.trim()) {
    claimMappings.roles = form.rolesClaim.trim();
  }

  const extras = {};

  // Keycloak
  if (
    form.providerType === "KEYCLOAK" &&
    form.realm.trim()
  ) {
    extras.realm = form.realm.trim();
  }

  // Entra ID
  if (
    form.providerType === "ENTRA_ID" &&
    form.tenantId.trim()
  ) {
    extras.tenantId = form.tenantId.trim();
  }

  // AWS Cognito
  if (form.providerType === "COGNITO") {
    if (form.region.trim()) {
      extras.region = form.region.trim();
    }

    if (form.userPoolId.trim()) {
      extras.userPoolId = form.userPoolId.trim();
    }

    if (form.domain.trim()) {
      extras.domain = form.domain.trim();
    }
  }

  const tenantId = form.tenantId.trim();
  const region = form.region.trim();
  const userPoolId = form.userPoolId.trim();
  const domain = form.domain.trim();

  // Replace provider-specific placeholders
  const resolvePlaceholders = (value) => {
    let result = value;

    // Entra ID
    if (
      form.providerType === "ENTRA_ID" &&
      tenantId
    ) {
      result = result.replaceAll(
        "{tenantId}",
        tenantId
      );
    }

    // AWS Cognito
    if (form.providerType === "COGNITO") {
      if (region) {
        result = result.replaceAll(
          "{region}",
          region
        );
      }

      if (userPoolId) {
        result = result.replaceAll(
          "{userPoolId}",
          userPoolId
        );
      }

      if (domain) {
        result = result.replaceAll(
          "{domain}",
          domain
        );
      }
    }

    return result;
  };

  return {
    name: form.name.trim(),

    tenantKey: form.tenantKey.trim(),

    providerType: form.providerType,

    authMode: form.authMode,

    enabled: form.enabled,

    issuerUri: resolvePlaceholders(
      form.issuerUri.trim()
    ),

    clientId: form.clientId.trim(),

    clientSecret: form.clientSecret.trim(),

    scopes,

    redirectUri: form.redirectUri.trim(),

    postLoginRedirectUri:
      form.postLoginRedirectUri.trim() || undefined,

    endSessionEndpoint:
      form.endSessionEndpoint.trim()
        ? resolvePlaceholders(
            form.endSessionEndpoint.trim()
          )
        : undefined,

    claimMappings,

    extras,
  };
}

/**
 * Convert a saved API configuration
 * back into the form structure.
 *
 * Client secret is intentionally left blank
 * because the API may redact it.
 */
export function formFromSaved(saved) {
  const validProviders = [
    "KEYCLOAK",
    "COGNITO",
    "ENTRA_ID",
    "AUTH0",
    "GENERIC_OIDC",
  ];

  const provider = validProviders.includes(
    saved.providerType
  )
    ? saved.providerType
    : "GENERIC_OIDC";

  const base = presetFor(provider);

  const extras = saved.extras ?? {};
  const claims = saved.claimMappings ?? {};

  return {
    ...base,

    name: saved.name,

    tenantKey: saved.tenantKey,

    providerType: provider,

    authMode:
      saved.authMode === "HANDOVER"
        ? "HANDOVER"
        : "LOCAL_JWT",

    enabled: saved.enabled,

    issuerUri:
      saved.issuerUri ?? "",

    clientId:
      saved.clientId ?? "",

    // Keep secret empty because API may redact it.
    clientSecret: "",

    scopes: (
      saved.scopes ?? [
        "openid",
        "profile",
        "email",
      ]
    ).join(", "),

    redirectUri:
      saved.redirectUri ?? base.redirectUri,

    postLoginRedirectUri:
      saved.postLoginRedirectUri ??
      base.postLoginRedirectUri,

    endSessionEndpoint:
      saved.endSessionEndpoint ?? "",

    realm:
      extras.realm ?? "",

    tenantId:
      extras.tenantId ?? "",

    region:
      extras.region ?? "",

    userPoolId:
      extras.userPoolId ?? "",

    domain:
      extras.domain ?? "",

    rolesClaim:
      claims.roles ?? base.rolesClaim,
  };
}

const PROVIDER_DISPLAY = {
  KEYCLOAK: "Keycloak",
  ENTRA_ID: "Entra ID",
  COGNITO: "AWS Cognito",
  AUTH0: "Auth0",
  GENERIC_OIDC: "OIDC",
};

/**
 * Create demo organization/tenant mapping
 * for a saved IdP.
 *
 * Organization ID: acme
 */
export function tenantMappingFor(form) {
  const tenantKey = form.tenantKey.trim();

  return {
    organizationId: "acme",

    organizationName: "Acme Corp",

    emailDomains: [
      "acme.com",
    ],

    defaultTenantKey: tenantKey,

    enabled: true,

    providers: [
      {
        tenantKey,

        displayName:
          PROVIDER_DISPLAY[form.providerType] ??
          form.providerType,

        primary: true,
      },
    ],
  };
}

